import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { supabaseClient } from "@/lib/supabase";
import type { Json } from "@/types/database/database";

interface DeleteScheduleParams {
    scheduleId: string;
    replacementScheduleId?: string;
}

interface TemplateScheduleUsage {
    template_id: string;
    template_name: string | null;
    settings: unknown;
}

export interface ScheduleDeleteUsage {
    trackerCount: number;
    backupTrackerCount: number;
    entryCount: number;
    templateCount: number;
    templates: TemplateScheduleUsage[];
    total: number;
}

function replaceScheduleInTemplateSettings(settings: unknown, scheduleId: string, replacementScheduleId: string) {
    if (!settings || typeof settings !== "object" || Array.isArray(settings)) return settings;

    const nextSettings = { ...settings } as Record<string, unknown>;
    const trackers = nextSettings.trackers;

    if (!Array.isArray(trackers)) return settings;

    nextSettings.trackers = trackers.map((tracker) => {
        if (!tracker || typeof tracker !== "object" || Array.isArray(tracker)) return tracker;
        const nextTracker = { ...tracker } as Record<string, unknown>;
        if (nextTracker.schedule_id === scheduleId) {
            nextTracker.schedule_id = replacementScheduleId;
        }
        return nextTracker;
    });

    return nextSettings;
}

function templateUsesSchedule(settings: unknown, scheduleId: string) {
    if (!settings || typeof settings !== "object" || Array.isArray(settings)) return false;
    const trackers = (settings as { trackers?: unknown }).trackers;
    if (!Array.isArray(trackers)) return false;

    return trackers.some((tracker) => {
        return Boolean(
            tracker &&
            typeof tracker === "object" &&
            !Array.isArray(tracker) &&
            (tracker as { schedule_id?: unknown }).schedule_id === scheduleId
        );
    });
}

async function countRows(table: "trackers" | "entries", column: string, scheduleId: string) {
    const { count, error } = await supabaseClient
        .from(table)
        .select("*", { count: "exact", head: true })
        .eq(column, scheduleId);

    if (error) {
        throw new Error(`Failed to count ${table}: ${error.message}`);
    }

    return count ?? 0;
}

export async function selectScheduleDeleteUsage(scheduleId: string): Promise<ScheduleDeleteUsage> {
    const [trackerCount, backupTrackerCount, entryCount, templatesResponse] = await Promise.all([
        countRows("trackers", "schedule_id", scheduleId),
        countRows("trackers", "back_up_schedule_id", scheduleId),
        countRows("entries", "schedule_id", scheduleId),
        supabaseClient.from("templates").select("template_id, template_name, settings").not("settings", "is", null),
    ]);

    if (templatesResponse.error) {
        throw new Error(`Failed to check template settings: ${templatesResponse.error.message}`);
    }

    const templates = ((templatesResponse.data ?? []) as TemplateScheduleUsage[]).filter((template) =>
        templateUsesSchedule(template.settings, scheduleId)
    );
    const templateCount = templates.length;
    const total = trackerCount + backupTrackerCount + entryCount + templateCount;

    return {
        trackerCount,
        backupTrackerCount,
        entryCount,
        templateCount,
        templates,
        total,
    };
}

export async function deleteSchedule({ scheduleId, replacementScheduleId }: DeleteScheduleParams) {
    if (replacementScheduleId === scheduleId) {
        throw new Error("Choose a different replacement schedule");
    }

    const usage = await selectScheduleDeleteUsage(scheduleId);

    if (usage.total > 0 && !replacementScheduleId) {
        throw new Error("Choose a replacement schedule before deleting this schedule");
    }

    if (replacementScheduleId) {
        const { error: trackersError } = await supabaseClient
            .from("trackers")
            .update({ schedule_id: replacementScheduleId })
            .eq("schedule_id", scheduleId);
        if (trackersError) throw new Error(`Failed to replace tracker schedules: ${trackersError.message}`);

        const { error: backupTrackersError } = await supabaseClient
            .from("trackers")
            .update({ back_up_schedule_id: replacementScheduleId })
            .eq("back_up_schedule_id", scheduleId);
        if (backupTrackersError) {
            throw new Error(`Failed to replace backup tracker schedules: ${backupTrackersError.message}`);
        }

        const { error: entriesError } = await supabaseClient
            .from("entries")
            .update({ schedule_id: replacementScheduleId })
            .eq("schedule_id", scheduleId);
        if (entriesError) throw new Error(`Failed to replace entry schedules: ${entriesError.message}`);

        for (const template of usage.templates) {
            const settings: Json = replaceScheduleInTemplateSettings(
                template.settings,
                scheduleId,
                replacementScheduleId
            ) as Json;
            const { error: templateError } = await supabaseClient
                .from("templates")
                .update({ settings })
                .eq("template_id", template.template_id);

            if (templateError) {
                throw new Error(
                    `Failed to update template "${template.template_name ?? template.template_id}": ${templateError.message}`
                );
            }
        }
    }

    const { error: scheduleError } = await supabaseClient.from("schedules").delete().eq("schedule_id", scheduleId);

    if (scheduleError) {
        throw new Error(`Failed to delete schedule: ${scheduleError.message}`);
    }

    return { success: true };
}

export function useScheduleDeleteUsageQuery(scheduleId: string, enabled: boolean) {
    return useQuery({
        queryKey: ["schedule-delete-usage", scheduleId],
        queryFn: () => selectScheduleDeleteUsage(scheduleId),
        enabled: enabled && Boolean(scheduleId),
    });
}

export function useDeleteScheduleMutation() {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteSchedule,
        onSuccess: () => {
            toast.success(toastCopy.scheduleUpdatedSuccess);
            void queryClient.invalidateQueries({ queryKey: ["time-templates"] });
        },
        onError: (error) => {
            toast.error(toastCopy.failedDeleteSchedule(error.message));
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: ["schedule"] });
            void queryClient.invalidateQueries({ queryKey: ["schedules"] });
            void queryClient.invalidateQueries({ queryKey: ["diaries"] });
            void queryClient.invalidateQueries({ queryKey: ["templates"] });
        },
    });
}
